import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import { Box, Typography } from '@mui/material';

import VerticalSlider from '../item/slider_vertical'
import Progress from '../item/progress'

export default function Scatter(props) {

    const [series, setSeries] = useState([]);
    const [colors, setColors] = useState([]);
    const [seriesXMax, setSeriesXMax] = useState([]);
    const [seriesYMax, setSeriesYMax] = useState([]);
    const [value, setValue] = useState([0, props.distance]);
    const [tooltipInfo, setTooltipInfo] = useState(null);
    const [isSlider, setIsSlider] = useState(false);
    const [isNoData, setIsNoData] = useState(false);

    const initializedRef = useRef(false);
    // チャートごとの一意のキー
    const chartId = useId();

    const aryMax = function (a, b) { return Math.max(a, b); }

    const getSeries = (chartData) => {
        setSeries(props.getSeries(chartData, isSlider, value));
    };

    const getColors = (chartData) => {
        setColors(props.getColors(chartData, isSlider, value));
    };

    // チャート情報を格納
    const setChartData = (chartData) => {
        const isOver = props.distance < chartData.length
        setIsSlider(isOver)
        getSeries(chartData)
        getColors(chartData)
    }

    // ローディング終了
    const loadingEnd = () => {
        props.setReady(prev => {
            const updated = { ...prev };
            props.isReadyKey.forEach(key => {
                if (key in updated) {
                    updated[key] = true;
                }
            });
            return updated;
        });
    }

    // データ更新が完了したらローディング終了
    useEffect(() => {
        loadingEnd()
    }, [series]);

    useEffect(() => {
        if (props.scatterData === null || props.scatterData.length === 0) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.scatterData && props.scatterData.length > 0) {
            setChartData(props.scatterData)
        }
        setIsNoData(false)
        loadingEnd()
    }, [props.scatterData]);

    useEffect(() => {
        if (props.scatterData === null || props.scatterData.length === 0) {
            setIsNoData(true)
            loadingEnd()
            return
        }
        if (props.scatterData && props.scatterData.length > 0) {
            setChartData(props.scatterData)
        }
        setIsNoData(false)
        loadingEnd()
        return () => {
            ApexCharts.exec(chartId, 'destroy'); // 明示的に破棄
        };
    }, []);

    useEffect(() => {
        // 初期表示時のみX軸とY軸の最大値を取得
        if (!initializedRef.current && props.scatterData && props.scatterData.length > 0) {

            // X軸のみの配列を取得
            const x_ary = props.x_ary(props.scatterData);
            // X軸最大値を取得
            const x_max = x_ary.reduce(aryMax)

            if (x_max < props.maxValX) {
                setSeriesXMax(props.maxValX + 1)
            }
            else {
                setSeriesXMax(x_max)
            }

            // Y軸のみの配列を取得
            const y_ary = props.y_ary(props.scatterData);
            // Y軸最大値を取得
            const y_max = y_ary.reduce(aryMax)
            if (y_max < props.maxValY) {
                setSeriesYMax(props.maxValY + 1)
            }
            else {
                setSeriesYMax(y_max)
            }

            initializedRef.current = true;
        }
    }, [props.scatterData]);

    const options = useMemo(() => ({
        chart: {
            id: chartId,
            toolbar: {
                show: false,
            },
            type: 'scatter',
            zoom: {
                enabled: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    // ローディング開始
                    props.setReady(prev => {
                        const updated = Object.fromEntries(
                            Object.keys(prev).map(key => [key, false])
                        );
                        return updated;
                    });
                    const seriesIndex = config.seriesIndex;
                    const dataPointIndex = config.dataPointIndex;
                    if (seriesIndex === -1 || dataPointIndex === -1) return; // クリック対象がない場合ガード
                    props.scatterClickEvent(series, seriesIndex);
                },
                // TODO 複数選択に対応するなら以下かもしれない
                // selection: (chartContext, { xaxis, yaxis }) => {
                //     console.log(chartContext)
                //     const selectedSeries = series.filter(s => {
                //         const [x, y] = s.data[0];
                //         return (
                //         x >= xaxis.min && x <= xaxis.max &&
                //         y >= yaxis.min && y <= yaxis.max
                //         );
                //     });

                //     if (selectedSeries.length > 0) {
                //         // 中央を計算（または1つ目を使う）
                //         const centerLng = selectedSeries[0].arealng;
                //         const centerLat = selectedSeries[0].arealat;
                //         props.setRiskMapCenter([centerLng, centerLat]);

                //         // 地図操作など（必要に応じて）
                //         props.setSelectSubCity(selectedSeries.map(s => s.cityCode));
                //         props.setShouldClearSelect(false);
                //         props.setSelectLayer(SR_LAYER_LIST.find(layer => layer.index.toString() === props.defaultUnit) || SR_LAYER_LIST[1]);
                //         props.setSelectArea([]);
                //         props.setSelectMsh([]);
                //     }
                //     // ズーム解除
                //     setTimeout(() => {
                //         ApexCharts.exec(chartId, 'resetZoom');
                //     }, 10);
                // },
                // // TODO 複数選択に対応するなら以下かもしれない
                // selection: (chartContext, { xaxis, yaxis }) => {
                //     console.log(chartContext)
                //     console.log(xaxis)
                //     console.log(yaxis)

                //     // ...
                // }
                //   ,      mouseMove: function(event, chartContext, opts) {
                //     console.log(event)
                //     console.log(chartContext)
                //     console.log(opts)
                //     // The last parameter opts contains additional information like `seriesIndex` and `dataPointIndex` for cartesian charts.
                //   }
                dataPointMouseEnter: (event, chartContext, config) => {
                    const item = props.scatterData?.[config.seriesIndex];
                    if (!item) return;

                    setTooltipInfo({
                        x: event.clientX,
                        y: event.clientY,
                        data: props.tooltipInfo(item)
                    });
                    },
                    mouseLeave: () => {
                    setTooltipInfo(null);
                }
            }
        },
        grid: {
            show: true,
            xaxis: {
                lines: {
                    show: false // 縦のグリッド線
                }
            },
            yaxis: {
                lines: {
                    show: false // 横のグリッド線
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        fill: {
            opacity: 0.8
        },
        legend: {
            show: false,
        },
        colors: colors,
        markers: {
            size: 3,
            strokeColors: ['#a9a9a9'],
            strokeWidth: [1],
        },
        xaxis: {
            show: true,
            min: 0,
            max: seriesXMax,
            tickAmount: 3,
            axisBorder: {
                show: true,
            },
            axisTicks: {
                show: false,
            },
            crosshairs: {
                show: false  // 縦の点線を表示させない
            },
            title: {
                text: props.textX,
                style: {
                    color: '#FFFFFF'
                }
            },
            labels: {
                show: true,
                style: {
                    colors: '#FFFFFF',
                },
                formatter: function (val) {
                    const shownLabels = [0, 50, 100, 150, 200, 250, 300];
                    const rounded = Math.round(val / 50) * 50;
                    return shownLabels.includes(rounded) ? rounded.toString() : '';
                }
            },
        },
        yaxis: {
            show: true,
            min: 0,
            max: seriesYMax,
            tickAmount: 4,
            axisBorder: {
                show: true, // show y-axis line
            },
            axisTicks: {
                show: false,
            },
            title: {
                text: props.textY,
                offsetX: 5,
                style: {
                    color: '#FFFFFF'
                }
            },
            labels: {
                show: true,
                style: {
                    colors: '#FFFFFF',
                },
                formatter: function (val) {
                    const shownLabels = [0, 20, 40, 60];
                    const rounded = Math.round(val / 20) * 20;
                    return shownLabels.includes(rounded) ? rounded.toString() : '';
                }
            },
        },
        tooltip: {
            enabled: false,
            // shared: false,
            // custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            //     const item = props.scatterData?.[seriesIndex];
            //     if (!item) return '';

            //     const name = item.name;
            //     const h24 = item.h1h24_val[1];
            //     const h1 = item.h1h24_val[0];
            //     const srVal = item.sr_val;
            //     const srLevel = item.sr_level;
            //     const address = item.address || '-';
            //     const date = item.date || '-';

            //     return `
            //     <div style="padding: 6px; font-size: 12px;">
            //         <div style="font-size: 14px;">雨量計名: <strong>${name}</strong></div>
            //         <div style="margin: 4px 0;"></div>
            //         <div>場所: <strong>${address}</strong></div>
            //         <div>${props.label}: <strong>${srLevel}</strong></div>
            //         <div>土壌雨量指数: <strong>${srVal}</strong></div>
            //         <div>1時間雨量: <strong>${h1}mm</strong></div>
            //         <div>24時間雨量: <strong>${h24}mm</strong></div>
            //         <div>日時: <strong>${date}</strong></div>
            //     </div>
            //     `;
            // }
        },
        annotations: {
            yaxis: [
                {
                    y: props.maxValY,
                    borderColor: '#FFFFFF',
                    strokeDashArray: 4,
                    label: {
                        style: {
                            background: '#000000',
                            color: '#FFFFFF',
                            fontSize: '12px'
                        },
                        text: `${props.maxValY}mm`
                    }
                }
            ],
            xaxis: [
                {
                    x: props.maxValX,
                    borderColor: '#FFFFFF',
                    strokeDashArray: 4,
                    label: {
                        style: {
                            background: '#000000',
                            color: '#FFFFFF',
                            fontSize: '12px',
                        },
                        text: `${props.maxValX}mm`
                    }
                },
            ],
        },
    }), [props.scatterData, series, colors]);

    // チャートのデータが更新されたら強制再描画
    useEffect(() => {
        if (props.scatterData !== null && props.scatterData !== undefined) {
            // props.selectTelCode の変更に応じた fetch 再実行
            ApexCharts.exec(chartId,
                "updateSeries", series);
        }
    }, [props.scatterData]);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    height: '100%',
                    overflow: 'hidden', // スクロール非表示
                }}
            >
                {/* チャート部分 */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    {!props.isReadyFlg ?
                        <Progress />
                        // <ChartSkeleton />
                        :
                        isNoData ?
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold',
                                    color: 'white',
                                }}
                            >
                                対象データがありません
                            </Typography>
                            :
                            <Box sx={{ position: 'relative', flex: 1, overflow: 'hidden', height: "100%" }}>
                                <Chart
                                    options={options}
                                    series={series}
                                    type="scatter"
                                    height="100%"
                                    width="100%"
                                    style={{ visibility: props.isReadyFlg ? 'visible' : 'hidden' }}
                                />
                                {tooltipInfo && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 20,
                                            left: '70%',
                                            transform: 'translateX(-50%)',
                                            width: '100%',
                                            maxWidth: 200,
                                            whiteSpace: props.whiteSpace,
                                            overflow: 'normal',
                                            textOverflow: 'ellipsis',
                                            backgroundColor: 'rgba(255, 255, 255, 1)',
                                            color: '#000',
                                            padding: '6px',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        {props.tooltipContent(tooltipInfo, props.label)}
                                    </Box>
                                )}
                            </Box>
                    }
                </Box>
                {/* スライダー部分 */}
                {isSlider &&
                    <Box
                        sx={{
                            width: '20px', // スライダー幅
                            marginLeft: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <VerticalSlider
                            data={props.scatterData}
                            value={value}
                            setValue={setValue}
                            distance={props.distance}
                            selectCitys={props.selectCitys}
                        />
                    </Box>
                }
            </Box>
        </>
    );
}